import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_URL } from "../config";

const FEATURE_SET = [
  "Advanced Load Filtering",
  "Automated Carrier Package Creation and Submission",
  "Backhaul Load Search",
  "Booking and Trip Analytics",
  "Unlimited Text and E-mail Load Recommendations",
  "Unlimited Load Access to Top Load Boards",
  "AI-Load Booking Assistant",
];

const TRIAL_FEATURE_SET = [
  "Limited Access to Top-Load Boards",
  "Saving Loads",
  "Carrier Package Upload",
  "Dedicated Customer Support",
];

const getDispatcherPrice = (count) => {
  if (count >= 5) return 0;
  return Math.max(0, 50 - count * 10);
};

const getDispatcherPriceId = (price) =>
  ({
    50: "price_1RihT0Q7ZC8Kt2tas02Tx4nb",
    40: "price_1RiiqsQ7ZC8Kt2tap8exzH43",
    30: "price_1RiirjQ7ZC8Kt2taO5f7pass",
    20: "price_1RiislQ7ZC8Kt2tawVITIn5J",
    10: "price_1Riiu5Q7ZC8Kt2taDQXvYGs5",
    0: "price_1RiiuUQ7ZC8Kt2taOOUq73Rg",
  }[price]);

const getPlans = (accountType, truckerCount = 0, hasHadTrial = false) => {
  if (accountType === "carrier") {
    return [
      {
        name: "14-Day Free Trial",
        // Trial has no monthly price; it's $0 for 14 days
        priceLabel: "$0 for 14 days",
        price: 0,
        interval: null,
        features: TRIAL_FEATURE_SET,
        buttonText: hasHadTrial
          ? "Trial Unavailable"
          : "Start 14-Day Free Trial",
        priceId: "trial",
        popular: true,
        tier: 1,
      },
      {
        name: "Premium",
        price: 100,
        interval: "month",
        features: FEATURE_SET,
        buttonText: "Subscribe",
        priceId: "price_1RihW4Q7ZC8Kt2taae8Fua0k",
        tier: 2,
      },
    ];
  }

  // dispatcher
  const currentPrice = getDispatcherPrice(truckerCount);
  const nextPrice =
    truckerCount >= 5 ? 0 : getDispatcherPrice(truckerCount + 1);
  const priceId = getDispatcherPriceId(currentPrice);

  return [
    {
      name: "14-Day Free Trial",
      priceLabel: "$0 for 14 days",
      price: 0,
      interval: null,
      features: FEATURE_SET,
      buttonText: hasHadTrial ? "Trial Unavailable" : "Start 14-Day Free Trial",
      priceId: "trial",
      popular: true,
      tier: 1,
    },
    {
      name: "Premium",
      price: currentPrice,
      interval: "month",
      features: [
        ...FEATURE_SET,
        truckerCount > 0 &&
          `${truckerCount} Trucker${truckerCount > 1 ? "s" : ""} Connected`,
        truckerCount < 5 &&
          `Connect ${5 - truckerCount} more trucker${
            5 - truckerCount > 1 ? "s" : ""
          } for free plan!`,
        nextPrice < currentPrice &&
          `Next tier: $${nextPrice}/month with ${truckerCount + 1} trucker${
            truckerCount + 1 > 1 ? "s" : ""
          }`,
      ].filter(Boolean),
      buttonText: "Subscribe",
      priceId,
      tier: 2,
    },
  ];
};

export default function Pricing() {
  const { user } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [showTrialPopup, setShowTrialPopup] = useState(false);
  const [showTrialUsedPopup, setShowTrialUsedPopup] = useState(false);

  // subscription_type: 0 = free, 1 = trial, 2 = premium
  const userTier = user?.subscription_type || 0;
  const hasHadTrial = !!user?.has_had_trial;

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    console.log(userTier);
    console.log(user?.subscription_end_date);
    if (userTier !== 1 || !user?.subscription_end_date) return 0;

    const endDate = new Date(user.subscription_end_date);
    const now = new Date();

    // Set both dates to start of day for accurate day comparison
    const endDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = endDay - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  useEffect(() => {
    const fetchCarriers = async () => {
      if (user?.account_type === "dispatcher") {
        try {
          const token = localStorage.getItem("access_token");
          const response = await axios.get(`${API_URL}/user/carriers`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCarriers(response.data);
        } catch (err) {
          console.error("Error fetching carriers:", err);
        }
      }
    };
    fetchCarriers();
  }, [user]);

  // Show popup for users who have used trial and are now on free tier
  useEffect(() => {
    if (userTier === 0 && hasHadTrial) {
      const timer = setTimeout(() => setShowTrialUsedPopup(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [userTier, hasHadTrial]);

  useEffect(() => {
    // Show popup for eligible users (no subscription and no prior trial)
    if (userTier === 0 && !hasHadTrial) {
      const timer = setTimeout(() => setShowTrialPopup(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [userTier, hasHadTrial]);

  const plans = getPlans(user?.account_type, carriers.length, hasHadTrial);

  const getButtonProps = (plan) => {
    // Current plan badge/disable
    if (
      (plan.tier === 2 && userTier === 2) ||
      (plan.tier === 1 && userTier === 1)
    ) {
      return {
        text: "Current Plan",
        disabled: true,
        style: {
          background: "var(--chrome)",
          color: "var(--muted)",
          cursor: "not-allowed",
        },
      };
    }

    // Trial button rules
    if (plan.priceId === "trial") {
      if (hasHadTrial || userTier > 0) {
        return {
          text: hasHadTrial ? "Trial Used" : "Not Available",
          disabled: true,
          style: {
            background: "var(--chrome)",
            color: "var(--muted)",
            cursor: "not-allowed",
          },
        };
      }
      return {
        text: "Start 14-Day Free Trial",
        disabled: false,
        style: {
          background: "var(--success)",
          color: "white",
          border: "none",
          cursor: "pointer",
        },
      };
    }

    // Premium button rules
    if (plan.tier === 2) {
      if (userTier > plan.tier) {
        return {
          text: "Included",
          disabled: true,
          style: {
            background: "var(--chrome)",
            color: "var(--muted)",
            cursor: "not-allowed",
          },
        };
      }
      return {
        text: userTier === 0 ? "Subscribe" : "Upgrade to Premium",
        disabled: false,
        style: {
          background: "var(--surface)",
          color: "var(--text)",
          border: "2px solid var(--chrome)",
          cursor: "pointer",
        },
      };
    }

    return { text: "Select", disabled: false, style: {} };
  };

  const handleSubscribe = async (priceId) => {
    try {
      if (!user) {
        alert("Please log in first.");
        return;
      }

      // Determine if this is a trial request based on priceId
      const isTrial = priceId === "trial";

      // Both trial and premium now use Stripe checkout
      const response = await axios.post(
        `${API_URL}/stripe/create-checkout-session`,
        {
          customerEmail: user.email,
          isTrial: isTrial,
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        alert("Failed to get checkout URL. Please try again.");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 min-h-[90vh] flex flex-col">
      <div className="text-center mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "var(--text)" }}>
          Choose Your Plan
        </h1>
        <p className="text-base sm:text-lg lg:text-xl max-w-2xl mx-auto px-4" style={{ color: "var(--muted)" }}>
          Start with a <strong>14-day free trial</strong> to test drive our features{" "}
          <span className="hidden sm:inline"><br /></span>
          or jump straight to <strong>Premium</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto w-full">
        {plans.map((plan) => {
          return (
            <div
              key={plan.name}
              data-plan-tier={plan.tier}
              className="relative flex flex-col h-full rounded-2xl p-6 sm:p-8 lg:p-10 border transition-all duration-200"
              style={{
                background: "var(--background)",
                borderColor: "var(--chrome)",
              }}
            >
              {(userTier === 2 && plan.tier === 2) ||
              (userTier === 1 && plan.tier === 1) ? (
                <div
                  className="absolute -top-3 left-4 sm:left-6 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg"
                  style={{
                    background:
                      userTier === 1
                        ? "linear-gradient(135deg, var(--primary), var(--secondary))"
                        : "var(--success)",
                  }}
                >
                  {userTier === 1 && trialDaysRemaining > 0
                    ? `Trial: ${
                        trialDaysRemaining === 1
                          ? "Last day"
                          : `${trialDaysRemaining} days left`
                      }`
                    : "Current Plan"}
                </div>
              ) : null}

              <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: "var(--text)" }}>
                {plan.name}
              </h2>

              <div
                className={`text-4xl sm:text-5xl lg:text-6xl font-bold flex items-baseline gap-2 flex-wrap ${
                  plan.priceId === "trial" ? "mb-2" : "mb-6 sm:mb-8"
                }`}
                style={{ color: "var(--text)" }}
              >
                {plan.priceId === "trial" ? (
                  <span className="text-3xl sm:text-4xl">{plan.priceLabel}</span>
                ) : (
                  <>
                    <span className="text-xl sm:text-2xl">$</span>
                    {plan.price}
                    {plan.price > 0 && (
                      <span className="text-base sm:text-lg font-normal" style={{ color: "var(--muted)" }}>
                        /{plan.interval}
                      </span>
                    )}
                    {user?.account_type === "dispatcher" &&
                      plan.price < 50 &&
                      plan.price > 0 && (
                        <div className="flex items-baseline ml-2 line-through font-bold" style={{ color: "var(--muted)" }}>
                          <span className="text-xl sm:text-2xl">$</span>
                          <span className="text-4xl sm:text-5xl lg:text-6xl">50</span>
                          <span className="text-base sm:text-lg ml-1">/{plan.interval}</span>
                        </div>
                      )}
                  </>
                )}
              </div>

              {plan.priceId === "trial" && (
                <div className="text-lg font-bold flex items-center gap-2 mb-6" style={{ color: "var(--success)" }}>
                </div>
              )}

              <ul className="list-none p-0 mb-6 sm:mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 py-2 sm:py-2.5 text-sm sm:text-base" style={{ color: "var(--text)" }}>
                    <i className="fas fa-check-circle text-base sm:text-lg flex-shrink-0" style={{ color: "var(--primary)" }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.priceId)}
                disabled={getButtonProps(plan).disabled}
                className="w-full rounded-xl px-6 py-3 sm:py-4 text-base sm:text-lg font-bold transition-all duration-200"
                style={getButtonProps(plan).style}
              >
                {getButtonProps(plan).text}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-sm sm:text-base text-center mt-8 sm:mt-12 max-w-2xl mx-auto px-4" style={{ color: "var(--muted)" }}>
        The trial is free for 14 days.
        <br />
        Premium continues with uninterrupted access.
      </p>

      {showTrialPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
          <div
            className="rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl"
            style={{ backgroundColor: "var(--background)" }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--text)" }}>
              🎁 Try Premium Free for 14 Days
            </h2>
            <p className="text-base sm:text-lg mb-6" style={{ color: "var(--text)" }}>
              Access every feature.
            </p>
            <ul className="list-none p-0 mb-6">
              <li className="text-sm sm:text-base mb-3" style={{ color: "var(--text)" }}>
                ✅ 7 limited load searches per day
              </li>
              <li className="text-sm sm:text-base mb-3" style={{ color: "var(--text)" }}>
                ✅ Saving Loads
              </li>
              <li className="text-sm sm:text-base mb-3" style={{ color: "var(--text)" }}>
                ✅ Carrier Package Upload
              </li>
              <li className="text-sm sm:text-base mb-3" style={{ color: "var(--text)" }}>
                ✅ Dedicated Customer Support
              </li>
            </ul>
            <p className="text-xs sm:text-sm text-center mb-6" style={{ color: "var(--muted)" }}>
              <strong>No hidden fees. Cancel anytime in one click.</strong>
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  setShowTrialPopup(false);
                  await handleSubscribe("trial");
                }}
                className="w-full rounded-xl px-6 py-3 sm:py-4 text-base sm:text-lg font-bold cursor-pointer transition-all duration-200 text-white border-none"
                style={{ background: "var(--primary)" }}
              >
                Start Free Trial
              </button>
              <button
                onClick={() => setShowTrialPopup(false)}
                className="w-full rounded-xl px-6 py-3 sm:py-4 text-base sm:text-lg font-bold cursor-pointer transition-all duration-200 bg-transparent border-2"
                style={{ color: "var(--text)", borderColor: "var(--chrome)" }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Used Popup */}
      {showTrialUsedPopup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-4">
          <div
            className="rounded-2xl p-6 sm:p-10 max-w-lg w-full text-center shadow-2xl border"
            style={{
              background: "var(--background)",
              borderColor: "var(--border)",
            }}
          >
            <div className="text-4xl sm:text-5xl mb-5">🎯</div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-5" style={{ color: "var(--text)" }}>
              Trial Period Complete
            </h2>
            <p className="text-base sm:text-lg mb-8 leading-relaxed" style={{ color: "var(--text)" }}>
              Your 14-day free trial has ended.
              <br />
              <br />
              Upgrade to Premium to continue enjoying all our features, or feel free to email{" "}
              <a
                href="mailto:albert@cinesis.ai"
                className="font-bold no-underline"
                style={{ color: "var(--primary)" }}
              >
                albert@cinesis.ai
              </a>{" "}
              if you have any questions about your trial or need help with upgrading.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowTrialUsedPopup(false)}
                className="bg-transparent border-2 rounded-xl px-6 py-3 sm:px-7 sm:py-3.5 text-base sm:text-lg font-semibold cursor-pointer transition-all duration-200 hover:opacity-90"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = "var(--primary)";
                  e.target.style.backgroundColor = "var(--primary)";
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = "var(--border)";
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "var(--text)";
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowTrialUsedPopup(false);
                  // Scroll to premium plans
                  const premiumSection = document.querySelector('[data-plan-tier="2"]');
                  if (premiumSection) {
                    premiumSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="border-none text-white rounded-xl px-6 py-3 sm:px-7 sm:py-3.5 text-base sm:text-lg font-semibold cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
                style={{
                  background: "var(--primary)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                View Premium Plans
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
